import { Component, computed, effect, inject, OnDestroy, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { UiAlertComponent, UiEmptyStateComponent, UiLoadingComponent } from '@design-system';
import { catchError, EMPTY, finalize, from, map, mergeMap, Observable, of, switchMap, tap, timer } from 'rxjs';
import { IntradayChartPoint } from '../../models/intraday-chart.model';
import { CommodityAccessService } from '../../services/commodity-access.service';
import { SymbolsService } from '../../services/symbols.service';
import { firstPositivePrice, MarketDataStore } from '../../core/market-data';
import { SymbolCardComponent } from './components/symbol-card/symbol-card.component';
import { changePercent, mapSymbolToMarketSymbol, MarketSymbol, withPinsApplied } from './components/symbol.model';
import { MarketHeaderEventConsumerService } from './events/market-header-event-consumer.service';
import { MarketHeaderEventProducerService } from './events/market-header-event-producer.service';
import { readMarketAccessNoticeDismissed, writeMarketAccessNoticeDismissed } from './market-access-notice.storage';
import { MarketPinsService } from './services/market-pins.service';
import { MarketViewService } from './services/market-view.service';

/** Intraday-chart requests run per symbol, so cap how many are in flight at once. */
const SPARKLINE_REQUEST_CONCURRENCY = 6;

/** How often the intraday-chart series is re-fetched for the listed symbols. */
const SPARKLINE_POLL_INTERVAL_MS = 60_000;

@Component({
	selector: 'app-market',
	imports: [SymbolCardComponent, UiLoadingComponent, UiEmptyStateComponent, UiAlertComponent],
	templateUrl: './market.component.html',
	providers: [MarketHeaderEventProducerService, MarketHeaderEventConsumerService, MarketViewService]
})
export class MarketComponent implements OnDestroy {
	private readonly symbolsService = inject(SymbolsService);
	private readonly marketData = inject(MarketDataStore);
	private readonly marketHeaderProducer = inject(MarketHeaderEventProducerService);
	private readonly marketHeaderConsumer = inject(MarketHeaderEventConsumerService);
	private readonly marketViewService = inject(MarketViewService);
	private readonly marketPins = inject(MarketPinsService);
	private readonly commodityAccess = inject(CommodityAccessService);

	/**
	 * The market list as the API sent it — by traded value, descending. Pins are
	 * not stamped in here: they are a stored setting, folded in by {@link items},
	 * so a pin never has to be written back into the market data.
	 */
	private readonly symbols = signal<MarketSymbol[]>([]);

	readonly loading = signal(false);

	readonly error = signal(false);

	/** What the page renders: the API's list with the trader's pins lifted to the top. */
	readonly items = computed(() => withPinsApplied(this.symbols(), this.marketPins.pinnedSymbols()));

	/** Symbols whose sparkline series is polled; replaced whenever the symbol list reloads. */
	private readonly sparklineIsins = signal<readonly string[]>([]);

	/** Bumped to ask for the market list again; each change starts a fresh request. */
	private readonly reloadTrigger = signal(0);

	/** Spinner replaces the list only while the first response is in flight. */
	readonly showLoading = computed(() => this.loading() && this.symbols().length === 0);

	readonly showEmptyState = computed(() => !this.loading() && !this.error() && this.items().length === 0);

	/**
	 * This browser session's dismissal of the «عدم دسترسی» notice. Read once as the page
	 * builds — nothing else writes it — so closing the notice only has to flip this signal
	 * and mirror it into storage.
	 */
	private readonly accessNoticeDismissed = signal(readMarketAccessNoticeDismissed());

	/**
	 * The notice is about the customer rather than the market data, so it stands over the
	 * loader, an error and the list alike — and it waits for the profile, so a customer who
	 * turns out to hold the کد never sees it flash.
	 */
	readonly showAccessNotice = computed(() => this.commodityAccess.denied() && !this.accessNoticeDismissed());

	constructor() {
		toSignal(
			this.marketViewService
				.connectAllDeposits()
				.pipe(tap(message => this.applyPriceUpdate(message.ci, message.ltp, message.lsp)))
		);

		effect(() => {
			const isLoading = this.loading();

			this.marketHeaderProducer.configureMarketHeader({
				headerLeftSectionItems: [
					{ iconName: 'refresh', eventId: 'refresh', ariaLabel: 'بروزرسانی', reloading: isLoading }
				]
			});
		});

		toSignal(
			this.marketHeaderConsumer.headerAction$.pipe(
				tap(event => {
					switch (event?.data?.eventId) {
						case 'refresh':
							this.onRetry();
							break;
						case 'messages':
						case 'menu':
							break;
					}

					if (event) {
						this.marketHeaderProducer.clearEventSnapshot(event.id);
					}
				})
			)
		);

		this.watchSymbols();

		this.pollSparklines();
	}

	/**
	 * Loads the market list on start and on every refresh. `switchMap` drops an in-flight
	 * request when a newer refresh arrives, so repeated taps can't land out of order.
	 */
	private watchSymbols(): void {
		toSignal(
			toObservable(this.reloadTrigger).pipe(
				tap(() => {
					this.loading.set(true);
					this.error.set(false);
				}),
				switchMap(() =>
					this.marketData.getSymbols().pipe(
						catchError(() => {
							this.error.set(this.symbols().length === 0);

							return EMPTY;
						}),
						finalize(() => this.loading.set(false))
					)
				),
				tap(symbols => {
					this.symbols.set(symbols.map(symbol => mapSymbolToMarketSymbol(symbol)));
					this.sparklineIsins.set(symbols.map(symbol => symbol.isin));
				})
			)
		);
	}

	/**
	 * Re-fetches every listed symbol's sparkline series on a 10s interval, restarting the
	 * schedule when the symbol list changes. Polling the whole batch (rather than each symbol
	 * on its own timer) keeps the concurrency cap meaningful.
	 */
	private pollSparklines(): void {
		toSignal(
			toObservable(this.sparklineIsins).pipe(
				switchMap(isins =>
					isins.length
						? timer(0, SPARKLINE_POLL_INTERVAL_MS).pipe(switchMap(() => this.loadSparklines(isins)))
						: EMPTY
				),
				tap(({ isin, trades }) => this.applySparkline(isin, trades))
			)
		);
	}

	/**
	 * Fills each card's sparkline from `/symbols/{isin}/intraday-chart`. Cards are patched
	 * as their series arrives, and a failed symbol just keeps its empty sparkline.
	 */
	private loadSparklines(isins: readonly string[]): Observable<{ isin: string; trades: IntradayChartPoint[] }> {
		return from(isins).pipe(
			mergeMap(
				isin =>
					this.symbolsService.getIntradayChart(isin).pipe(
						map(chart => ({ isin, trades: chart?.points ?? [] })),
						catchError(() => of({ isin, trades: [] as IntradayChartPoint[] }))
					),
				SPARKLINE_REQUEST_CONCURRENCY
			)
		);
	}

	/** Cards are keyed by isin (`MarketSymbol.id`), which is what the series is fetched by. */
	private applySparkline(isin: string, trades: IntradayChartPoint[]): void {
		this.symbols.update(items =>
			items.map(item => (item.id === isin ? { ...item, sparklineTrades: trades } : item))
		);
	}

	/**
	 * `lsp` is yesterday's closing price, which is both the change-percent reference and the
	 * sparkline's baseline — so the baseline tracks the feed rather than staying pinned to the
	 * value the REST snapshot happened to carry when the list loaded.
	 *
	 * A tick can omit either number; the card keeps what it already shows rather than falling
	 * back to the `-` the number pipe renders for a missing value.
	 */
	private applyPriceUpdate(contractId: number, ltp: number, lastSettlementPrice: number): void {
		this.symbols.update(items =>
			items.map(item => {
				if (item.contractId !== contractId) {
					return item;
				}

				// A tick with no trade in it (pre-open, after the close) must not blank the last
				// price, nor recompute a change the backend has already zeroed for the night.
				if (!(ltp > 0)) {
					return item;
				}

				const baseline = firstPositivePrice(lastSettlementPrice, item.baseline);

				return { ...item, price: ltp, baseline, changePercent: changePercent(ltp, baseline) };
			})
		);
	}

	onRetry(): void {
		this.reloadTrigger.update(count => count + 1);
	}

	/**
	 * Closes the notice for the rest of this session. The choice is not carried further than
	 * that on purpose: the handoff asks for the notice back next time the app is opened.
	 */
	onAccessNoticeDismissed(): void {
		this.accessNoticeDismissed.set(true);
		writeMarketAccessNoticeDismissed();
	}

	ngOnDestroy(): void {
		this.marketHeaderProducer.resetHeader();
	}

	/**
	 * Pin or unpin a symbol. Applied without a confirmation — the icon flipping
	 * and the card moving are the feedback — and persisted, so the choice
	 * survives leaving the page and closing the app.
	 */
	togglePin(itemId: string): Promise<boolean> {
		return this.marketPins.toggle(itemId);
	}
}
